<template>
    <div
        id="app"
        @mousedown="handleMouseDown"
        @mouseup="handleMouseUp"
    >
        <VBugReport
            v-if="isShowNavbar"
            :is-navbar-collapsed="(isEditMode && isMenuCollapsed) || (!isEditMode)"
        />
        <div id="copy-table"></div>
        <VTooltip
            :title="tooltip.title"
            :content="tooltip.content"
            :event="tooltip.event"
            :clickable="tooltip.clickable"
            :offsets="tooltip.offsets"
            :direction="tooltip.direction"
            :showDelay="tooltip.showDelay"
        ></VTooltip>
        <NavBar
            v-if="isShowNavbar"
            :menu="menuFiltered"
            :is-menu-collapsed="isMenuCollapsed"
            :route-path="$route.path"
            :menu-permissions="menuPermissions"
            :is-edit-mode="isEditMode"
            @changePage="changePage"
            @changeMenuVisibility="changeMenuVisibility"
        ></NavBar>
        
        <div
            class="page"
            :class="{'page__menu-collapsed':isShowCollapseClass && isShowNavbar}"
        >
            <keep-alive
                :exclude="[
                'Integrations',
                'Applications',
                'Users'
                ]"
            >
                <router-view
                    :include="'dashboard'"
                    :key="$route.path"
                    :isMenuCollapsed="isMenuCollapsed"
                    :is-edit-mode="isEditMode"
                    ref="currentPage"
                    @showTooltip="showTooltip"
                    @updateEditMode="updateEditMode"
                    @removeToastr="removeToastr"
                />
            </keep-alive>
        </div>
        <VNotification/>
    </div>
</template>

<script>
    import menu from './menu';
    import NavBar from './App/NavBar';
    import VTooltip from './components/VTooltip';
    import {
        getIsEditMode,
    } from '@/api/apiServices';
    import VNotification from '@/components/VNotification';
    import {getUser, getAdminPages} from '@/api/apiServices';
    import {setEmitUpdateMeta} from './api/apiServices';
    import {eventBus} from './main';
    import VBugReport from './components/VBugReport';
    
    export default {
        name: 'App',
        components: {
            VNotification,
            NavBar,
            VTooltip,
            VBugReport,
        },
        data() {
            return {
                isMenuCollapsed: true,
                menu: Object.freeze(menu),
                menuFiltered: [],
                tooltip: {
                    event: '',
                    content: '',
                    title: '',
                    clickable: false,
                    offsets: {
                        top: 0,
                        left: 0,
                    },
                    showDelay: 1000,
                    direction: 'bottom',
                },
                menuPermissions: {},
                defaultTooltip: {},
                isEditMode: false,
            };
        },
        computed: {
            isShowNavbar() {
                return !this.$route.meta.isFullPage;
            },
            isShowCollapseClass() {
                return this.isMenuCollapsed && this.isEditMode;
            },
        },
        watch: {
            isEditMode: {
                handler() {
                    this.menuPermissions = this.updateMenuPermissions();
                },
            },
        },
        created() {
            setEmitUpdateMeta(this.updatedMeta);
            document.addEventListener('visibilitychange', this.handleAppFocus);
            this.updatedMeta();
            this.defaultTooltip = _.cloneDeep(this.tooltip);
        },
        beforeDestroy() {
            document.removeEventListener('visibilitychange', this.handleAppFocus);
        },
        methods: {
            handleMouseDown(event) {
                eventBus.$emit('App-event-mousedown', event);
            },
            handleMouseUp(event) {
                eventBus.$emit('App-event-mouseup', event);
            },
            handleAppFocus(event) {
                if (document.hidden) {
                    eventBus.$emit('App-event-lostFocus', event);
                }
                
            },
            changePage(routePath) {
                if (this.$route.path !== routePath) {
                    this.$router.push({path: routePath, query: {staging: this.$route.query.staging}});
                    this.emitCurrentPage();
                    localStorage.setItem('lastPage', routePath);
                    this.removeToastr();
                }
            },
            emitCurrentPage() {
                this.$nextTick(() => {
                    if (this.$refs.currentPage.pageLoaded) {
                        this.$refs.currentPage.pageLoaded();
                    }
                });
            },
            changeMenuVisibility() {
                this.isMenuCollapsed = !this.isMenuCollapsed;
            },
            showTooltip(event, tooltipData) {
                Object.assign(this.tooltip, this.defaultTooltip, tooltipData);
                this.tooltip.event = event;
            },
            updateMenuPermissions() {
                return {
                    isEditMode: this.isEditMode,
                };
            },
            updatedMeta() {
                this.updateMenuItems();
                this.updateEditMode();
            },
            updateEditMode() {
                this.isEditMode = getIsEditMode();
            },
            updateMenuItems() {
                const user = getUser();
                const adminPages = getAdminPages();
                const menu = _.cloneDeep(this.menu);
                if (user.id) {
                    menu[0].items = menu[0].items.filter((item) => {
                        return !adminPages.includes(item.router.trim().toLowerCase()) || user.owner;
                    });
                    
                    this.menuFiltered = menu;
                }
            },
            removeToastr() {
                this.$toastr.removeByType('error');
            },
        },
    };
</script>

<style lang="scss">
    html {
        scroll-behavior: smooth;
    }
    
    body {
        width: 100%;
    }
    
    #app {
        font-family: 'Quicksand', sans-serif;
        font-size: 14px;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-align: center;
        color: $dark-blue-color;
        display: flex;
        height: 100%;
        min-height: 100vh;
    }
    
    #copy-table {
        position: absolute;
        background: none;
        opacity: 0;
        pointer-events: none;
        
        table, tr, td {
            background: none;
            border: none;
        }
    }
    
    .page {
        width: 100%;
        background: white;
        
        .page-body {
            padding: 0 20px 30px 20px;
            //overflow-y: auto;
            //height: calc(100vh - 70px);
        }
        
        &__menu-collapsed {
            width: calc(100% - 51px);
            
            .Vheader {
                margin-left: 0;
            }
        }
    }
    
    .default-outline {
        outline: 1px solid #1194cc;
        border-radius: 5px;
    }

    .error-outline {
      outline: 1px solid red;
      border-radius: 5px;
    }

</style>
